import { emptySplitApi } from '../../../../store/emptyApi';

// Type definitions for Assessment Period Metric (period-specific values)
export interface AssessmentPeriodMetric {
  id: number;
  metric: number;
  metric_name?: string;
  metric_label?: string | null;
  assessment_period: number;
  assessment_period_name?: string;
  organization?: number;
  organization_name?: string;
  team?: number | null;
  team_name?: string | null;
  metric_type_display?: string;
  value: number;
  target_value: number | null;
  data_source: string | null;
  collected_date: string | null;
  notes: string | null;
  created_by: number | null;
  created_by_email?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssessmentPeriodMetricList {
  id: number;
  metric: number;
  metric_name?: string;
  metric_label?: string | null;
  assessment_period: number;
  assessment_period_name?: string;
  organization?: number;
  organization_name?: string;
  team?: number | null;
  team_name?: string | null;
  metric_type?: number;
  metric_type_display?: string;
  value: number;
  target_value: number | null;
  data_source: string | null;
  collected_date: string | null;
  created_at: string;
}

export interface AssessmentPeriodMetricCreate {
  metric: number;
  assessment_period: number;
  organization: number;
  team?: number | null;
  value: number;
  target_value?: number | null;
  data_source?: string | null;
  collected_date?: string | null;
  notes?: string | null;
}

export interface AssessmentPeriodMetricUpdate {
  metric?: number;
  assessment_period?: number;
  organization?: number;
  team?: number | null;
  value?: number;
  target_value?: number | null;
  data_source?: string | null;
  collected_date?: string | null;
  notes?: string | null;
}

export interface BulkAssessmentPeriodMetricRequest {
  metrics: Array<{
    metric_id: number;
    assessment_period_id: number;
    value: number;
    target_value?: number | null;
    data_source?: string | null;
    collected_date?: string | null;
    notes?: string | null;
  }>;
}

export interface BulkAssessmentPeriodMetricResponse {
  created?: number;
  errors?: Array<{
    row: number;
    errors?: Record<string, string[]>;
    error?: string;
  }>;
  metrics?: AssessmentPeriodMetricList[];
}

export interface AssessmentPeriodMetricListParams {
  assessment_period_id?: number;
  metric_id?: number;
  organization_id?: number;
  team_id?: number | null;
}

// Route constants for assessment period metrics API
export const ASSESSMENT_PERIOD_METRICS_ROUTES = {
  base: '/api/admin/v1/assessment-period-metrics/',
  byId: (id: number) => `/api/admin/v1/assessment-period-metrics/${id}/`,
  bulkCreate: '/api/admin/v1/assessment-period-metrics/bulk-create/',
  export: '/api/admin/v1/assessment-period-metrics/export/',
} as const;

// Tag types for cache invalidation
export const AssessmentPeriodMetricApiTags = {
  AssessmentPeriodMetric: 'AssessmentPeriodMetric',
} as const;

const taggedApi = emptySplitApi.enhanceEndpoints({
  addTagTypes: Object.values(AssessmentPeriodMetricApiTags),
});

export const assessmentPeriodMetricsApi = taggedApi.injectEndpoints({
  endpoints: (build) => ({
    // List assessment period metrics
    getAssessmentPeriodMetrics: build.query<
      AssessmentPeriodMetricList[],
      AssessmentPeriodMetricListParams
    >({
      query: (params) => {
        // Build params object, only including defined values
        const queryParams: Record<string, string | number> = {};
        if (
          params.assessment_period_id !== undefined &&
          params.assessment_period_id !== null
        ) {
          queryParams.assessment_period_id = params.assessment_period_id;
        }
        if (params.metric_id !== undefined && params.metric_id !== null) {
          queryParams.metric_id = params.metric_id;
        }
        if (params.team_id !== undefined) {
          // Include team_id even if null (null means "Organization Only")
          // Use 'null' as a string to explicitly signal organization-only filter
          queryParams.team_id = params.team_id === null ? 'null' : params.team_id;
        }
        return {
          url: ASSESSMENT_PERIOD_METRICS_ROUTES.base,
          params: queryParams,
        };
      },
      // Ensure query key includes all filter params for proper cache invalidation
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { assessment_period_id, metric_id, team_id } = queryArgs;
        return `${endpointName}(${JSON.stringify({
          assessment_period_id: assessment_period_id ?? null,
          metric_id: metric_id ?? null,
          team_id: team_id ?? null,
        })})`;
      },
      providesTags: [AssessmentPeriodMetricApiTags.AssessmentPeriodMetric],
    }),

    // Get single assessment period metric
    getAssessmentPeriodMetric: build.query<
      AssessmentPeriodMetric,
      number
    >({
      query: (id) => ASSESSMENT_PERIOD_METRICS_ROUTES.byId(id),
      providesTags: (result, error, id) => [
        { type: AssessmentPeriodMetricApiTags.AssessmentPeriodMetric, id },
      ],
    }),

    // Create assessment period metric
    createAssessmentPeriodMetric: build.mutation<
      AssessmentPeriodMetric,
      AssessmentPeriodMetricCreate
    >({
      query: (data) => ({
        url: ASSESSMENT_PERIOD_METRICS_ROUTES.base,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        AssessmentPeriodMetricApiTags.AssessmentPeriodMetric,
      ],
    }),

    // Update assessment period metric
    updateAssessmentPeriodMetric: build.mutation<
      AssessmentPeriodMetric,
      { id: number; data: AssessmentPeriodMetricUpdate }
    >({
      query: ({ id, data }) => ({
        url: ASSESSMENT_PERIOD_METRICS_ROUTES.byId(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: AssessmentPeriodMetricApiTags.AssessmentPeriodMetric, id },
        AssessmentPeriodMetricApiTags.AssessmentPeriodMetric,
      ],
    }),

    // Partial update assessment period metric
    partialUpdateAssessmentPeriodMetric: build.mutation<
      AssessmentPeriodMetric,
      { id: number; data: Partial<AssessmentPeriodMetricUpdate> }
    >({
      query: ({ id, data }) => ({
        url: ASSESSMENT_PERIOD_METRICS_ROUTES.byId(id),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: AssessmentPeriodMetricApiTags.AssessmentPeriodMetric, id },
        AssessmentPeriodMetricApiTags.AssessmentPeriodMetric,
      ],
    }),

    // Delete assessment period metric
    deleteAssessmentPeriodMetric: build.mutation<void, number>({
      query: (id) => ({
        url: ASSESSMENT_PERIOD_METRICS_ROUTES.byId(id),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: AssessmentPeriodMetricApiTags.AssessmentPeriodMetric, id },
        AssessmentPeriodMetricApiTags.AssessmentPeriodMetric,
      ],
    }),

    // Bulk create assessment period metrics
    bulkCreateAssessmentPeriodMetrics: build.mutation<
      BulkAssessmentPeriodMetricResponse,
      BulkAssessmentPeriodMetricRequest
    >({
      query: (data) => ({
        url: ASSESSMENT_PERIOD_METRICS_ROUTES.bulkCreate,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        AssessmentPeriodMetricApiTags.AssessmentPeriodMetric,
      ],
    }),

    // Export assessment period metrics
    exportAssessmentPeriodMetrics: build.query<
      Blob | AssessmentPeriodMetricList[],
      {
        format?: 'csv' | 'json';
        params?: AssessmentPeriodMetricListParams;
      }
    >({
      query: ({ format = 'csv', params }) => ({
        url: ASSESSMENT_PERIOD_METRICS_ROUTES.export,
        params: {
          format,
          ...params,
        },
        responseHandler: async (response) => {
          if (format === 'csv') {
            return await response.blob();
          } else {
            return await response.json();
          }
        },
      }),
    }),
  }),
  overrideExisting: false,
});

// Export hooks for use in components
export const {
  useGetAssessmentPeriodMetricsQuery,
  useGetAssessmentPeriodMetricQuery,
  useCreateAssessmentPeriodMetricMutation,
  useUpdateAssessmentPeriodMetricMutation,
  usePartialUpdateAssessmentPeriodMetricMutation,
  useDeleteAssessmentPeriodMetricMutation,
  useBulkCreateAssessmentPeriodMetricsMutation,
  useLazyExportAssessmentPeriodMetricsQuery,
} = assessmentPeriodMetricsApi;

